import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithEmailAndPassword, sendPasswordResetEmail, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../firebase';
import { getEnvironment } from '../config/apiKeys';
import styles from './Auth.module.css';

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');
  
  // Password reset form state
  const [searchParams, setSearchParams] = useSearchParams();
  const [showResetForm, setShowResetForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  
  const navigate = useNavigate();

  // Check for password reset action code in URL
  useEffect(() => {
    const oobCode = searchParams.get('oobCode');
    const mode = searchParams.get('mode');
    
    if (oobCode && mode === 'resetPassword') {
      setShowResetForm(true);
      setShowForgotPassword(false);
    }
  }, [searchParams]);

  // Password strength validation
  const validatePasswordStrength = (password) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    return {
      isValid: minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar,
      minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSpecialChar
    };
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Check if Firebase auth is available
    if (!auth) {
      setError('Authentication service is not configured. Please contact support.');
      setLoading(false);
      return;
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      // Sign in successful - get user role to determine redirect
      await userCredential.user.getIdToken(true);
      const tokenResult = await userCredential.user.getIdTokenResult();
      const role = tokenResult.claims.role;
      
      // Redirect based on role
      if (role === 'admin_user') {
        navigate('/app/admin');
      } else {
        navigate('/app/mls-gpt');
      }
    } catch (error) {
      console.error('Sign in error:', error);
      
      // Handle specific Firebase auth errors
      switch (error.code) {
        case 'auth/user-not-found':
          setError('No account found with this email address.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password. Please try again.');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address.');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later.');
          break;
        case 'auth/user-disabled':
          setError('This account has been disabled.');
          break;
        default:
          setError('Failed to sign in. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');
    setResetSuccess(false);

    if (!resetEmail) {
      setResetError('Please enter your email address.');
      setResetLoading(false);
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail)) {
      setResetError('Please enter a valid email address.');
      setResetLoading(false);
      return;
    }

    // Check if Firebase auth is available
    if (!auth) {
      setResetError('Authentication service is not configured. Please contact support.');
      setResetLoading(false);
      return;
    }

    try {
      // Determine the correct domain based on environment
      const environment = getEnvironment();
      let continueUrl;
      
      if (typeof window !== 'undefined') {
        const protocol = window.location.protocol;
        const hostname = window.location.hostname;
        
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          continueUrl = `${protocol}//${hostname}:${window.location.port || 3000}/signin`;
        } else if (environment === 'staging') {
          continueUrl = 'https://vatesdomusstaging.com/signin';
        } else {
          continueUrl = 'https://vatesdomusai.com/signin';
        }
      } else {
        // Fallback for SSR
        continueUrl = environment === 'staging' 
          ? 'https://vatesdomusstaging.com/signin'
          : 'https://vatesdomusai.com/signin';
      }

      // Configure action code settings to use custom domain and handle in-app
      const actionCodeSettings = {
        url: continueUrl,
        handleCodeInApp: true, // Handle reset in our app, not Firebase's default handler
      };

      await sendPasswordResetEmail(auth, resetEmail, actionCodeSettings);
      setResetSuccess(true);
      setResetError('');
      // Clear the email after successful send (increased timeout to show spam notice longer)
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetEmail('');
        setResetSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Password reset error:', error);
      
      // Handle specific Firebase auth errors
      // SECURITY: Use generic message to prevent email enumeration
      switch (error.code) {
        case 'auth/user-not-found':
          // Generic message to prevent email enumeration
          setResetError('If an account exists with this email, a password reset link has been sent.');
          break;
        case 'auth/invalid-email':
          setResetError('Please enter a valid email address.');
          break;
        case 'auth/too-many-requests':
          setResetError('Too many requests. Please try again later.');
          break;
        default:
          // Generic message for all other errors to prevent information leakage
          setResetError('If an account exists with this email, a password reset link has been sent.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetPasswordLoading(true);
    setResetPasswordError('');
    setResetPasswordSuccess(false);

    if (!newPassword || !confirmNewPassword) {
      setResetPasswordError('Please enter and confirm your new password.');
      setResetPasswordLoading(false);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setResetPasswordError('Passwords do not match.');
      setResetPasswordLoading(false);
      return;
    }

    // Enhanced password strength validation
    const passwordValidation = validatePasswordStrength(newPassword);
    if (!passwordValidation.isValid) {
      const requirements = [];
      if (!passwordValidation.minLength) requirements.push('at least 8 characters');
      if (!passwordValidation.hasUpperCase) requirements.push('one uppercase letter');
      if (!passwordValidation.hasLowerCase) requirements.push('one lowercase letter');
      if (!passwordValidation.hasNumber) requirements.push('one number');
      if (!passwordValidation.hasSpecialChar) requirements.push('one special character');
      
      setResetPasswordError(`Password must contain: ${requirements.join(', ')}.`);
      setResetPasswordLoading(false);
      return;
    }

    const oobCode = searchParams.get('oobCode');
    if (!oobCode) {
      setResetPasswordError('Invalid reset link. Please request a new password reset.');
      setResetPasswordLoading(false);
      return;
    }

    // Basic oobCode format validation (alphanumeric and some special chars)
    if (!/^[A-Za-z0-9_-]+$/.test(oobCode)) {
      setResetPasswordError('Invalid reset link format. Please request a new password reset.');
      setResetPasswordLoading(false);
      return;
    }

    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setResetPasswordSuccess(true);
      setResetPasswordError('');
      
      // Clear URL parameters
      setSearchParams({});
      
      // Redirect to sign in after 3 seconds
      setTimeout(() => {
        setShowResetForm(false);
        setNewPassword('');
        setConfirmNewPassword('');
        navigate('/signin');
      }, 3000);
    } catch (error) {
      console.error('Password reset confirmation error:', error);
      
      switch (error.code) {
        case 'auth/expired-action-code':
          setResetPasswordError('The password reset link has expired. Please request a new one.');
          break;
        case 'auth/invalid-action-code':
          setResetPasswordError('Invalid reset link. Please request a new password reset.');
          break;
        case 'auth/weak-password':
          setResetPasswordError('Password is too weak. Please choose a stronger password.');
          break;
        default:
          setResetPasswordError('Failed to reset password. Please try again.');
      }
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // Get password strength indicator
  const getPasswordStrength = () => {
    if (!newPassword) return null;
    const validation = validatePasswordStrength(newPassword);
    const requirements = [
      { met: validation.minLength, text: '8+ characters' },
      { met: validation.hasUpperCase, text: 'Uppercase letter' },
      { met: validation.hasLowerCase, text: 'Lowercase letter' },
      { met: validation.hasNumber, text: 'Number' },
      { met: validation.hasSpecialChar, text: 'Special character' }
    ];
    return requirements;
  };

  // If showing reset form, render that instead
  if (showResetForm) {
    const passwordRequirements = getPasswordStrength();
    
    return (
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
          <div className={styles.resetPasswordHeader}>
            <div className={styles.resetIcon}>🔒</div>
            <h2>Reset Your Password</h2>
            <p className={styles.authSubtitle}>Enter your new secure password below</p>
          </div>
          
          {resetPasswordSuccess && (
            <div className={styles.successMessage}>
              <div className={styles.successIcon}>✓</div>
              <div>
                <strong>Password reset successful!</strong>
                <p>Redirecting to sign in...</p>
              </div>
            </div>
          )}
          
          {resetPasswordError && <div className={styles.errorMessage}>{resetPasswordError}</div>}
          
          <form onSubmit={handlePasswordReset} className={styles.authForm}>
            <div className={styles.formGroup}>
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="Enter your new password"
                disabled={resetPasswordLoading}
                minLength={8}
                autoComplete="new-password"
              />
              {passwordRequirements && (
                <div className={styles.passwordRequirements}>
                  <p className={styles.requirementsTitle}>Password must contain:</p>
                  <ul className={styles.requirementsList}>
                    {passwordRequirements.map((req, index) => (
                      <li key={index} className={req.met ? styles.requirementMet : styles.requirementUnmet}>
                        <span className={styles.requirementIcon}>{req.met ? '✓' : '○'}</span>
                        {req.text}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="confirmNewPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmNewPassword"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                placeholder="Confirm your new password"
                disabled={resetPasswordLoading}
                minLength={8}
                autoComplete="new-password"
              />
              {confirmNewPassword && newPassword === confirmNewPassword && (
                <div className={`${styles.formHelp} ${styles.success}`}>✓ Passwords match</div>
              )}
              {confirmNewPassword && newPassword !== confirmNewPassword && (
                <div className={`${styles.formHelp} ${styles.error}`}>✗ Passwords do not match</div>
              )}
            </div>
            
            <button 
              type="submit" 
              className={styles.authButton}
              disabled={resetPasswordLoading || !newPassword || !confirmNewPassword}
            >
              {resetPasswordLoading ? 'Resetting Password...' : 'Reset Password'}
            </button>
            
            <button
              type="button"
              onClick={() => {
                setSearchParams({});
                setShowResetForm(false);
                navigate('/signin');
              }}
              className={styles.linkButton}
              style={{ 
                marginTop: '1rem',
                display: 'block',
                width: '100%'
              }}
              disabled={resetPasswordLoading}
            >
              ← Back to Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.authContainer}>
      <div className={styles.authCard}>
        <h2>Sign In to Vates Domus AI</h2>
        <p className={styles.authSubtitle}>Access your real estate intelligence platform</p>
        
        {error && <div className={styles.errorMessage}>{error}</div>}
        
        {!showForgotPassword ? (
          <>
            <form onSubmit={handleSignIn} className={styles.authForm}>
              <div className={styles.formGroup}>
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                  disabled={loading}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setResetEmail(email); // Pre-fill with email if user has entered it
                    setError('');
                  }}
                  className={styles.linkButton}
                  style={{ 
                    marginTop: '0.5rem', 
                    fontSize: '0.85rem',
                    padding: 0,
                    textAlign: 'left'
                  }}
                  disabled={loading}
                >
                  Forgot Password?
                </button>
              </div>
              
              <button 
                type="submit" 
                className={styles.authButton}
                disabled={loading}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
            
            <div className={styles.authLinks}>
              <p>
                Don't have an account?{' '}
                <button 
                  onClick={() => navigate('/pricing')}
                  className={styles.linkButton}
                >
                  Sign Up
                </button>
              </p>
              <button 
                onClick={() => navigate('/')}
                className={styles.linkButton}
              >
                Back to Home
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.forgotPasswordHeader}>
              <div className={styles.resetIcon}>🔑</div>
              <h3>Reset Password</h3>
              <p className={styles.authSubtitle}>
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>
            
            <form onSubmit={handleForgotPassword} className={styles.authForm}>
              {resetSuccess && (
                <div className={styles.successMessage}>
                  <div className={styles.successIcon}>✓</div>
                  <div>
                    <strong>Password reset email sent!</strong>
                    <p>Please check your inbox and follow the instructions.</p>
                    <div className={styles.spamNotice}>
                      <span className={styles.spamIcon}>📧</span>
                      <span><strong>Tip:</strong> Don't see the email? Check your spam or junk folder.</span>
                    </div>
                  </div>
                </div>
              )}
              
              {resetError && <div className={styles.errorMessage}>{resetError}</div>}
              
              <div className={styles.formGroup}>
                <label htmlFor="resetEmail">Email</label>
                <input
                  type="email"
                  id="resetEmail"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                  disabled={resetLoading}
                />
              </div>
              
              <button 
                type="submit" 
                className={styles.authButton}
                disabled={resetLoading}
              >
                {resetLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetEmail('');
                  setResetError('');
                  setResetSuccess(false);
                }}
                className={styles.linkButton}
                style={{ 
                  marginTop: '1rem',
                  display: 'block',
                  width: '100%'
                }}
                disabled={resetLoading}
              >
                ← Back to Sign In
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default SignIn; 